import type { Mode } from "../../types/story";
import {
  collectDeviceInfo,
  newId,
  type EventBase,
  type EventType,
  type TelemetryEvent,
} from "./events";

/**
 * Telemetry queue.
 *
 * Durability model (Phase 0): every event is written to IndexedDB FIRST, then
 * a best-effort flush POSTs un-acked events to the endpoint. If the endpoint is
 * unreachable (offline, no endpoint configured), events simply stay queued and
 * are retried on the next flush / page load. The IndexedDB store is the source
 * of truth and can be exported to JSON/CSV at any time.
 *
 * Events are never dropped on the floor: even if IndexedDB is unavailable, we
 * fall back to an in-memory buffer so export still works within the session.
 */

/**
 * Distributive Omit so each member of the TelemetryEvent union keeps its own
 * discriminated fields (e.g. `device`, `sceneId`) after stripping the common
 * envelope. A plain `Omit<Union, K>` would collapse to only shared keys.
 */
type DistributiveOmit<T, K extends keyof never> = T extends unknown
  ? Omit<T, K>
  : never;
type EventInput = DistributiveOmit<
  TelemetryEvent,
  Exclude<keyof EventBase, "type">
>;

const DB_NAME = "tella-telemetry";
const DB_VERSION = 1;
const STORE = "events";

interface StoredEvent {
  /** Mirrors event.eventId; primary key. */
  eventId: string;
  /** true once the endpoint has acknowledged it. */
  flushed: boolean;
  event: TelemetryEvent;
}

/** Endpoint resolution: ?telemetry= URL override, else build-time env, else /api/telemetry. */
function resolveEndpoint(): string {
  const fromUrl = new URLSearchParams(window.location.search).get("telemetry");
  if (fromUrl) return fromUrl;
  const fromEnv = import.meta.env.VITE_TELEMETRY_ENDPOINT as string | undefined;
  if (fromEnv) return fromEnv;
  return "/api/telemetry";
}

function openDb(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    if (typeof indexedDB === "undefined") return resolve(null);
    let req: IDBOpenDBRequest;
    try {
      req = indexedDB.open(DB_NAME, DB_VERSION);
    } catch {
      return resolve(null);
    }
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "eventId" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => resolve(null);
  });
}

export interface TelemetryContext {
  family: string;
  child: string;
  storyId: string;
  mode: Mode;
}

export class Telemetry {
  private ctx: TelemetryContext;
  private sessionId = newId();
  private endpoint = resolveEndpoint();
  private dbPromise: Promise<IDBDatabase | null>;
  /** Fallback if IndexedDB is unavailable. */
  private memory: StoredEvent[] = [];
  private flushing = false;

  // Bound handlers kept so dispose() can remove them. A single App can open
  // several sessions in one visit (each library pick makes a fresh Telemetry);
  // without cleanup every pick would leak these listeners and keep an old queue
  // flushing on every online/visibility/pagehide event.
  private readonly onOnline = () => void this.flush();
  private readonly onVisibility = () => {
    if (document.visibilityState === "hidden") void this.flush();
  };
  private readonly onPagehide = () => this.beaconFlush();

  constructor(ctx: TelemetryContext) {
    this.ctx = ctx;
    this.dbPromise = openDb();

    // Flush opportunistically.
    window.addEventListener("online", this.onOnline);
    document.addEventListener("visibilitychange", this.onVisibility);
    // Last-ditch flush on unload (sendBeacon, best effort).
    window.addEventListener("pagehide", this.onPagehide);
  }

  /**
   * Detach this session's flush listeners. Call when replacing a session (e.g.
   * a new library pick) so listeners and the old in-memory queue don't pile up.
   * IndexedDB events already persist, so a disposed session loses nothing.
   */
  dispose(): void {
    window.removeEventListener("online", this.onOnline);
    document.removeEventListener("visibilitychange", this.onVisibility);
    window.removeEventListener("pagehide", this.onPagehide);
  }

  /** Build the common envelope for an event. */
  private base(type: EventType): EventBase {
    const now = Date.now();
    return {
      eventId: newId(),
      type,
      ts: now,
      tsIso: new Date(now).toISOString(),
      family: this.ctx.family,
      child: this.ctx.child,
      storyId: this.ctx.storyId,
      mode: this.ctx.mode,
      sessionId: this.sessionId,
    };
  }

  /** Record any well-typed event. Writes to store, then best-effort flush. */
  async record(partial: EventInput): Promise<void> {
    const event = { ...this.base(partial.type), ...partial } as TelemetryEvent;
    await this.put({ eventId: event.eventId, flushed: false, event });
    void this.flush();
  }

  // ---- Convenience helpers, one per spec'd event ------------------------

  sessionStart() {
    return this.record({ type: "sessionStart", device: collectDeviceInfo() });
  }
  sceneViewed(sceneId: string, dwellMs: number) {
    return this.record({ type: "sceneViewed", sceneId, dwellMs });
  }
  audioCompleted(sceneId: string) {
    return this.record({ type: "audioCompleted", sceneId });
  }
  choiceMade(sceneId: string, optionIndex: number) {
    return this.record({ type: "choiceMade", sceneId, optionIndex });
  }
  questionAnswered(qId: string, optionIndex: number, correct: boolean) {
    return this.record({ type: "questionAnswered", qId, optionIndex, correct });
  }
  questionSkipped(qId: string) {
    return this.record({ type: "questionSkipped", qId });
  }
  storyCompleted(path: string[], totalMs: number) {
    return this.record({ type: "storyCompleted", path, totalMs });
  }
  replay() {
    return this.record({ type: "replay" });
  }

  // ---- Storage ----------------------------------------------------------

  private async put(rec: StoredEvent): Promise<void> {
    const db = await this.dbPromise;
    if (!db) {
      this.memory.push(rec);
      return;
    }
    await new Promise<void>((resolve) => {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).put(rec);
      tx.oncomplete = () => resolve();
      tx.onerror = () => {
        this.memory.push(rec);
        resolve();
      };
    });
  }

  private async all(): Promise<StoredEvent[]> {
    const db = await this.dbPromise;
    if (!db) return [...this.memory];
    const fromDb = await new Promise<StoredEvent[]>((resolve) => {
      const tx = db.transaction(STORE, "readonly");
      const req = tx.objectStore(STORE).getAll();
      req.onsuccess = () => resolve((req.result as StoredEvent[]) ?? []);
      req.onerror = () => resolve([]);
    });
    return [...fromDb, ...this.memory];
  }

  private async markFlushed(ids: string[]): Promise<void> {
    if (!ids.length) return;
    const idSet = new Set(ids);
    for (const rec of this.memory) {
      if (idSet.has(rec.eventId)) rec.flushed = true;
    }
    const db = await this.dbPromise;
    if (!db) return;
    await new Promise<void>((resolve) => {
      const tx = db.transaction(STORE, "readwrite");
      const store = tx.objectStore(STORE);
      for (const id of ids) {
        const getReq = store.get(id);
        getReq.onsuccess = () => {
          const rec = getReq.result as StoredEvent | undefined;
          if (rec) {
            rec.flushed = true;
            store.put(rec);
          }
        };
      }
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    });
  }

  // ---- Flush ------------------------------------------------------------

  /** Best-effort POST of all un-flushed events. Never throws. */
  async flush(): Promise<void> {
    if (this.flushing) return;
    this.flushing = true;
    try {
      const pending = (await this.all()).filter((r) => !r.flushed);
      if (!pending.length) return;
      const events = pending.map((r) => r.event);
      const res = await fetch(this.endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ events }),
        keepalive: true,
      });
      if (res.ok) {
        await this.markFlushed(pending.map((r) => r.eventId));
      }
    } catch {
      // Offline / no endpoint: leave queued for next attempt.
    } finally {
      this.flushing = false;
    }
  }

  /** Fire-and-forget flush for page teardown. */
  private beaconFlush(): void {
    void (async () => {
      try {
        const pending = (await this.all()).filter((r) => !r.flushed);
        if (!pending.length) return;
        const body = JSON.stringify({ events: pending.map((r) => r.event) });
        if (navigator.sendBeacon) {
          navigator.sendBeacon(
            this.endpoint,
            new Blob([body], { type: "application/json" })
          );
        }
      } catch {
        /* ignore */
      }
    })();
  }

  // ---- Export -----------------------------------------------------------

  /** All recorded events (flushed or not), oldest first. */
  async exportEvents(): Promise<TelemetryEvent[]> {
    const recs = await this.all();
    return recs.map((r) => r.event).sort((a, b) => a.ts - b.ts);
  }
}
